import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemText,
  CircularProgress,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSnackbar } from 'notistack';
import type { BatchWorkflow, WorkflowStatus } from '../../types/workflow';
import { WorkflowStatusCodes } from '../../types/workflow/constants';
import { workflowService } from '../../services/workflowService';
import { WorkflowStatusChip } from './WorkflowStatusChip';

interface WorkflowStatusSelectProps {
  workflowId: number;
  statusCode: string;
  statusName?: string;
  /** Called with the updated workflow after a successful transition. */
  onChanged?: (workflow: BatchWorkflow) => void;
  size?: 'small' | 'medium';
}

/** Canonical pipeline order, used only to spot a backward move. */
const PIPELINE_ORDER: readonly string[] = Object.values(WorkflowStatusCodes);

/**
 * A transition that is hard to walk back gets a one-line confirm; everything
 * else is one click. Terminal statuses lock the workflow (no reopen path) and
 * backward moves leave side effects (draft PO/invoice lines, partner links)
 * in place.
 */
const needsConfirm = (from: string, target: WorkflowStatus): string | null => {
  if (target.isTerminal) {
    return `Mark this workflow as "${target.name}"? This is a final status and cannot be reopened.`;
  }
  const fromIdx = PIPELINE_ORDER.indexOf(from);
  const toIdx = PIPELINE_ORDER.indexOf(target.code);
  if (fromIdx >= 0 && toIdx >= 0 && toIdx < fromIdx) {
    return `Move this workflow back to "${target.name}"? Work already done at later stages is kept.`;
  }
  return null;
};

/**
 * WorkflowStatusSelect
 *
 * The status chip rendered as a one-click dropdown. Opening the menu fetches
 * the transitions the current user is allowed to make (role + forward-only
 * rules are enforced server-side); picking one calls the status endpoint
 * immediately with no notes. Every transition is still written to the
 * transition log and activity log by the backend.
 */
export const WorkflowStatusSelect: React.FC<WorkflowStatusSelectProps> = ({
  workflowId,
  statusCode,
  statusName,
  onChanged,
  size = 'small',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [transitions, setTransitions] = useState<WorkflowStatus[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<{ target: WorkflowStatus; message: string } | null>(null);

  const open = Boolean(anchorEl);

  const loadTransitions = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      setTransitions(await workflowService.getAvailableTransitions(workflowId));
    } catch {
      setTransitions(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (submitting) return;
    setAnchorEl(event.currentTarget);
    void loadTransitions();
  };

  const handleClose = () => setAnchorEl(null);

  const transition = async (target: WorkflowStatus) => {
    setSubmitting(true);
    try {
      const updated = await workflowService.updateStatus(workflowId, { newStatusId: target.id });
      enqueueSnackbar(`Status updated to "${target.name}"`, { variant: 'success' });
      onChanged?.(updated);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to update status', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelect = (target: WorkflowStatus) => {
    handleClose();
    const message = needsConfirm(statusCode, target);
    if (message) {
      setPending({ target, message });
      return;
    }
    void transition(target);
  };

  const handleConfirm = () => {
    const target = pending?.target;
    setPending(null);
    if (target) void transition(target);
  };

  return (
    <>
      <Tooltip title="Change status">
        <Box
          component="button"
          type="button"
          aria-label="Change status"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={handleOpen}
          disabled={submitting}
          data-testid="workflow-status-select"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            p: 0,
            border: 0,
            background: 'none',
            cursor: submitting ? 'progress' : 'pointer',
            '& .MuiChip-root': { cursor: 'inherit' },
          }}
        >
          <WorkflowStatusChip statusCode={statusCode} statusName={statusName} size={size} />
          {submitting ? (
            <CircularProgress size={14} sx={{ ml: 0.5 }} />
          ) : (
            <ArrowDropDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          )}
        </Box>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading…
          </MenuItem>
        )}
        {!loading && loadFailed && (
          <MenuItem onClick={() => void loadTransitions()}>
            <ListItemText primary="Failed to load statuses" secondary="Click to retry" />
          </MenuItem>
        )}
        {!loading && transitions?.length === 0 && (
          <MenuItem disabled>No transitions available</MenuItem>
        )}
        {!loading &&
          transitions?.map((status) => (
            <MenuItem key={status.id} onClick={() => handleSelect(status)}>
              <ListItemText primary={status.name} secondary={status.description} />
            </MenuItem>
          ))}
      </Menu>

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          <DialogContentText>{pending?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkflowStatusSelect;
